import { useState, useCallback, useEffect } from 'react';
import { ZoomClient } from 'index-types';

/**
 * This function monitors the active video and the active speaker.
 * It defines functions-setters and monitors the corresponding events in the client, and returns
 * true if speaker or video is set to something other than zero
 * @param zmClient , a zoom client currently employed
 * @returns boolean, true if something is active now
 */
export function useActiveVideo(zmClient: ZoomClient) {
  // these are states controllingc the current active video and speaker on screen
  const [activeVideo, setActiveVideo] = useState<number>(0);
  const [activeSpeaker, setActiveSpeaker] = useState<number>(0);

  /**
   * This function gets the payload with the user status
   * and synchronizes the state. It dispatches the active user id if it comes active
   * and zeroes the state otherwise. So it works twice when active user changes
   * @param payload , the obj with state and userid of active user event
   * @returns
   */
  const onVideoActiveChange = (payload: { state: string; userId: number }) => {
    const { state, userId } = payload;
    if (state === 'Active') {
      setActiveVideo(userId);
    } else if (state === 'Inactive') {
      setActiveVideo(0);
    }
  };
  // this is a memorized version of a function, and it always looks like this.
  const onVideoActiveChangeMemorized = useCallback(onVideoActiveChange, []);

  /**
   * This function is similar to the videacitve change. But the payload here is a list, and
   * the function takes the stack's first element and dispatches the active speaker.
   * @param payload, an array of dicts with userIds
   */
  const onActiveSpeakerChange = (payload: { userId: number }[]) => {
    if (Array.isArray(payload) && payload.length > 0) {
      const { userId } = payload[0];
      setActiveSpeaker(userId);
    }
  };

  // memorizing the funciton
  const onActiveSpeakerChangeMemorized = useCallback(onActiveSpeakerChange, []);

  // This is a callback which sets the corresponding function to monitor the events, and
  // if cleans, turns off the events. Bound to client changes, and function changes.
  useEffect(() => {
    zmClient.on('Video-active-change', onVideoActiveChangeMemorized);
    zmClient.on('active-speaker', onActiveSpeakerChangeMemorized);
    return () => {
      zmClient.off('Video-active-change', onVideoActiveChangeMemorized);
      zmClient.off('active-speaker', onActiveSpeakerChange);
    };
  }, [zmClient, onVideoActiveChangeMemorized, onActiveSpeakerChangeMemorized]);
  return activeVideo || activeSpeaker;
}
